import { useSyncExternalStore } from 'react';

/**
 * The customer's current dine-in table — persisted so it survives navigation and reloads
 * ("don't change" after a QR scan). Set from a scanned QR / `/t/:code` URL; read everywhere
 * (header, menu, orders, bill, bottom nav).
 *
 * Default is EMPTY (no table) — a customer who opens the app WITHOUT scanning a QR is NOT on a
 * table, so their order is a takeaway (not pinned to a phantom "Table 7"). Was hardcoded to
 * 'sg-07', which made every un-scanned order look like a dine-in for a table that doesn't exist —
 * it never showed on the floor, only as a notification. Mirrors the Flutter app's empty default.
 */
const KEY = 'thalios.table';
const DEFAULT_TABLE = '';

/**
 * What a table code can look like: letters, digits, dash, slash, underscore — the same shape the
 * API's own table-code rule allows, and short.
 *
 * Everything here is a guard around ONE failure: a QR token that could not be decrypted used to
 * be stored as if it were the table. `enc.…` is not a table code, but nothing checked, so it was
 * written to localStorage and stayed there — the header showed "T713157318113", ordering 400'd
 * on the table-code pattern, `auth/guest` 401'd ("that table isn't available"), and requesting
 * the bill 400'd. Reloading did not help; only clearing site data did. A value that isn't
 * code-shaped is not a table, and refusing it here is what stops one bad scan becoming a
 * permanent one.
 */
const CODE = /^[a-z0-9][a-z0-9\-/_]{0,39}$/;

/** True when `code` is shaped like a table code the API would accept. */
export function isTableCode(code: string | null | undefined): boolean {
  return CODE.test((code ?? '').trim().toLowerCase());
}

function safeRead(): string {
  try {
    const stored = window.localStorage.getItem(KEY) || DEFAULT_TABLE;
    // Self-heal: a session poisoned by an earlier build is dropped on the next load, so the
    // customer is a takeaway again (which works) rather than stuck on a table that never existed.
    if (stored && !isTableCode(stored)) {
      window.localStorage.removeItem(KEY);
      return DEFAULT_TABLE;
    }
    return stored;
  } catch {
    return DEFAULT_TABLE;
  }
}

let current = typeof window !== 'undefined' ? safeRead() : DEFAULT_TABLE;
const listeners = new Set<() => void>();

/** The current table code (lowercase, e.g. "sg-06"). */
export function getTable(): string {
  return current;
}

/**
 * Persist the current table (from a QR scan / URL). No-ops on empty or unchanged, and REFUSES
 * anything that isn't shaped like a table code — pass the decrypted code, never the `enc.…`
 * token. Returns whether the table was accepted, so a caller can tell the customer the scan
 * didn't work instead of silently opening a menu they can't order from.
 */
export function setTable(code: string | null | undefined): boolean {
  const c = (code ?? '').trim().toLowerCase();
  if (!c) return false;
  if (!isTableCode(c)) return false;
  if (c === current) return true;
  current = c;
  try {
    window.localStorage.setItem(KEY, c);
  } catch {
    /* storage disabled — keep it in memory */
  }
  listeners.forEach((fn) => fn());
  return true;
}

/** Leave the table — back to takeaway. */
export function clearTable(): void {
  if (!current) return;
  current = DEFAULT_TABLE;
  try {
    window.localStorage.removeItem(KEY);
  } catch {
    /* storage disabled — the in-memory value is already cleared */
  }
  listeners.forEach((fn) => fn());
}

function subscribe(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** React hook — the current table code, reactive to {@link setTable}. */
export function useTable(): string {
  return useSyncExternalStore(subscribe, getTable, () => DEFAULT_TABLE);
}
